<template>
  <div class="container-fluid transfer two-to-four-year-transfer-container">
    <div class="row">
      <div class="col-md-12 text-center">
        <h2>{{ heading }}</h2>
      </div>
    </div>

    <div v-if="isLoading" class="row">
      <div class="col-md-12 text-center mt-5">
        <div class="loader"></div>
        <p>Loading plan...</p>
      </div>
    </div>

    <div v-else-if="errorMsg" class="row">
      <div class="col-md-12 text-center mt-5">
        <h4 style="color: red;">{{ errorMsg }}</h4>
      </div>
    </div>

    <template v-else-if="plan">
      <div class="school-names">
        <span class="school-name set">
          <i class="fa-solid fa-school"></i>
          <span>{{ plan.startingSchool?.name }}</span>
        </span>
        <i class="fa-solid fa-arrow-right"></i>
        <span class="school-name set">
          <i class="fa-solid fa-school"></i>
          <span>{{ plan.destinationSchool?.name }}</span>
        </span>
      </div>

      <div class="row two-to-four-year-report-container">
        <div class="col-md-12 pl-0">
          <button
            @click="savePlan"
            class="btn btn-success mb-2 mr-2 ml-0"
            :disabled="isSaving"
          >
            <span v-if="isSaving">Saving...</span>
            <span v-else-if="saveSuccess">Changes Saved</span>
            <span v-else>Save Changes</span>
          </button>

          <button
            data-toggle="modal"
            data-target="#reportSummarModal"
            class="btn btn-primary mb-2"
            type="button"
          >
            Analyze transfer plan
          </button>
        </div>

        <div class="col-md-12 dp-visualization-container p-4">
          <DegreePlanVisualization
            v-if="planTermData.length"
            :planTermData="planTermData"
            :isTransferPlan="true"
            :hadTransfer="true"
            :readOnly="false"
          />
        </div>
      </div>

      <ReportSummaryModal :reportData="plan.transferPlanAnalysis || {}" />
    </template>

    <div v-else class="row">
      <div class="col-md-12 text-center mt-5">
        <h4>Plan not found.</h4>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import ReportSummaryModal from "@/components/TransferSummaryModal.vue";
// @ts-ignore
import { DegreePlanVisualization } from "@unm-idi/vue-degree-plan/vue-degree-plan.js";
import store from "@/store";
import { cleanPlanForSave, resolveS3SlugFromPlan } from "@/utils/planSerialization";

export default defineComponent({
  name: "PlanDetails",
  setup() {
    const route = useRoute();
    const planName = decodeURIComponent(String(route.params.planName || ""));
    const plan = ref<any>(null);
    const planTermData = ref<any[]>([]);
    const isLoading = ref(true);
    const errorMsg = ref("");
    const isSaving = ref(false);
    const saveSuccess = ref(false);

    const heading = computed(() =>
      plan.value?.planType === "completion"
        ? "Transfer evaluation plan"
        : "Two year to four year transfer plan"
    );

    onMounted(async () => {
      try {
        plan.value = await store.methods.fetchPlanDetail(planName);
        planTermData.value = plan.value.fourYearPlan || [];
      } catch (e: any) {
        console.error("Error loading plan:", e);
        errorMsg.value = e?.response?.data?.message || e.message || "Failed to load plan.";
      } finally {
        isLoading.value = false;
      }
    });

    async function savePlan() {
      if (!plan.value) return;
      try {
        isSaving.value = true;
        saveSuccess.value = false;

        const slugs = resolveS3SlugFromPlan(plan.value);

        const updatedPlan = {
          ...plan.value,
          s3Slug: slugs,
          fourYearPlan: cleanPlanForSave(planTermData.value),
        };

        await store.methods.uploadUserFile(
          updatedPlan,
          slugs.dest,
          slugs.start
        );

        saveSuccess.value = true;
        setTimeout(() => { saveSuccess.value = false; }, 3000);
      } catch (e: any) {
        console.error("Error saving plan:", e);
        errorMsg.value = e?.response?.data?.message || e.message || "Failed to save plan.";
      } finally {
        isSaving.value = false;
      }
    }

    return { plan, planTermData, isLoading, errorMsg, isSaving, saveSuccess, savePlan, heading };
  },
  components: {
    DegreePlanVisualization,
    ReportSummaryModal,
  },
});
</script>
